import { saveMarketingConsentFn } from "@/actions/marketing/handler";
import { useConsentStore } from "@/components/analytics/consent";
import { Button } from "@/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Cookie, X } from "lucide-react";
import { useEffect, useState } from "react";

export function CookieConsent() {
  const [mounted, setMounted] = useState(false);
  const consent = useConsentStore((s) => s.consent);
  const setConsent = useConsentStore((s) => s.setConsent);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setMounted(true);
    const reopen = () => setEditing(true);
    window.addEventListener("thecoach:open-cookie-settings", reopen);
    return () =>
      window.removeEventListener("thecoach:open-cookie-settings", reopen);
  }, []);

  const saveMutation = useMutation({
    mutationFn: (value: "granted" | "denied") =>
      saveMarketingConsentFn({ data: { consent: value } }),
  });

  const choose = (value: "granted" | "denied") => {
    setConsent(value);
    setEditing(false);
    saveMutation.mutate(value);
  };

  // SSR ve ilk render sırasında null dönerek hydration hatasını önle.
  if (!mounted) {
    return null;
  }

  // Kullanıcı zaten kabul ettiyse veya reddettiyse ekranda hiçbir şey gösterme
  if (consent && !editing) {
    return null;
  }

  return (
    <aside className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-3xl rounded-2xl border border-border bg-card/98 p-4 shadow-2xl backdrop-blur sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Cookie className="hidden h-7 w-7 shrink-0 text-primary sm:block" />
        <div className="flex-1 space-y-1">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground text-sm">
              Çerez tercihleri
            </h2>
            <button
              type="button"
              onClick={() => choose("denied")}
              className="text-muted-foreground transition-colors hover:text-foreground sm:hidden"
              aria-label="Kapat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-muted-foreground text-xs leading-relaxed sm:text-sm">
            Zorunlu çerezler sitenin çalışması için kullanılır. İzin verirseniz
            Google ve Meta ölçüm araçları reklam performansını anlamamıza
            yardımcı olur. Tercihinizi daha sonra değiştirebilirsiniz.{" "}
            <Link
              to="/privacy"
              className="text-primary underline underline-offset-4 hover:opacity-80"
            >
              Gizlilik politikası
            </Link>
          </p>
        </div>
        <div className="grid shrink-0 grid-cols-2 gap-2 sm:flex sm:items-center">
          <Button
            size="sm"
            variant="destructive"
            onClick={() => choose("granted")}
            className="w-full sm:w-auto"
            disabled={saveMutation.isPending}
          >
            Kabul et
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => choose("denied")}
            className="w-full sm:w-auto"
            disabled={saveMutation.isPending}
          >
            Reddet
          </Button>
        </div>
      </div>
    </aside>
  );
}
